import {
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuRootProps,
	type MenuVariantProps,
	stopPropagation,
} from "@overmap-ai/blocks"
import type { AssetTypeStatus, ProcedureType, Stored } from "@overmap-ai/common"
import { memo, type ReactElement } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { AssetTypeStatusGroup } from "../MenuGroups/AssetTypeStatusGroup"
import { ProcedureTypeSelectGroup } from "../MenuSelectGroups/ProcedureTypeSelectGroup"

enum BulkAssetActionMenuPages {
	INITIAL = "initial",
	STATUS = "status",
	INITIATIVES = "initiatives",
}

interface AssetBulkDropdownMenuProps extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	children: ReactElement
	onExportPdf?: () => void
	onExportCsv?: () => void
	onLinkIssue?: () => void
	onDelete?: () => void
	statuses: Stored<AssetTypeStatus>[]
	onStatusSelect: (status: Stored<AssetTypeStatus>) => void
	procedureTypes: Stored<ProcedureType>[]
	onProcedureTypeSelect: (procedureType?: Stored<ProcedureType>) => void
}

export const AssetBulkDropdownMenu = memo((props: AssetBulkDropdownMenuProps) => {
	const {
		children,
		disabled,
		size,
		variant,
		radius,
		accentColor,
		onExportPdf,
		onExportCsv,
		onLinkIssue,
		onDelete,
		statuses,
		onStatusSelect,
		procedureTypes,
		onProcedureTypeSelect,
		...rest
	} = props

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content
				size={size}
				variant={variant}
				accentColor={accentColor}
				radius={radius}
				onClick={stopPropagation}
			>
				<Menu.Scroll>
					<Menu.Pages defaultPage={BulkAssetActionMenuPages.INITIAL}>
						<Menu.PageContent page={BulkAssetActionMenuPages.INITIAL}>
							<Menu.PageTrigger page={BulkAssetActionMenuPages.INITIATIVES}>
								<LuIcon icon={modelIconMapping.procedureInitiave} />
								Start initiative
							</Menu.PageTrigger>
							<Menu.PageTrigger page={BulkAssetActionMenuPages.STATUS}>
								<LuIcon icon={modelIconMapping.status} />
								Change status
							</Menu.PageTrigger>

							{onLinkIssue ? (
								<Menu.Item onSelect={onLinkIssue}>
									<LuIcon icon={modelIconMapping.issue} />
									Link issue
								</Menu.Item>
							) : null}

							{!!onExportPdf || !!onExportCsv ? (
								<>
									<Menu.Separator />
									{onExportPdf ? (
										<Menu.Item onSelect={onExportPdf}>
											<LuIcon icon={modelIconMapping.download} />
											Export PDF
										</Menu.Item>
									) : null}
									{onExportCsv ? (
										<Menu.Item onSelect={onExportCsv}>
											<LuIcon icon={modelIconMapping.download} />
											Export CSV
										</Menu.Item>
									) : null}
								</>
							) : null}

							{onDelete ? (
								<>
									<Menu.Separator />
									<Menu.Item onSelect={onDelete}>
										<LuIcon icon={modelIconMapping.delete} />
										Delete
									</Menu.Item>
								</>
							) : null}
						</Menu.PageContent>
						<Menu.PageContent page={BulkAssetActionMenuPages.STATUS}>
							<AssetTypeStatusGroup
								assetTypeStatuses={statuses}
								onSelectAssetTypeStatus={onStatusSelect}
							/>
						</Menu.PageContent>
						<Menu.PageContent page={BulkAssetActionMenuPages.INITIATIVES}>
							<ProcedureTypeSelectGroup
								required={false}
								value={undefined}
								procedureTypes={procedureTypes}
								onValueChange={onProcedureTypeSelect}
							/>
						</Menu.PageContent>
					</Menu.Pages>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
AssetBulkDropdownMenu.displayName = "AssetBulkDropdownMenu"
